import { NavLink } from "react-router";

import { cn, colorTransitionClassName, focusRingClassName } from "@ceedero/ui";

import { useTranslation } from "@/localization/hooks/use-translation";
import { ROUTES } from "@/navigation/constants/routes";

/**
 * The pages of this application.
 *
 * Drawn as the desktop draws its navigation rail, laid along the top instead:
 * the current page is filled with the palette's primary, so where the reader is
 * reads as a fact at a glance and in the colour they chose, and every other
 * label stays at full strength — held at `--muted-foreground`, the unchosen
 * ones would be drawn exactly as this theme draws a disabled control. The hover
 * is a thin wash of the text colour, which reads the same on every palette in
 * either theme.
 */
export const AppNavigation = ({ className }: { className?: string }) => {
  const { t } = useTranslation();

  const links = [
    // `end` only on the home link: every path starts with "/", so without it the
    // home tab is marked current on every page of the application.
    { end: true, label: t("navigation.home"), to: ROUTES.home },
    { label: t("navigation.catalog"), to: ROUTES.catalog },
    { label: t("navigation.shelves"), to: ROUTES.shelves },
    { label: t("navigation.devices"), to: ROUTES.devices },
  ];

  return (
    <nav aria-label={t("navigation.label")} className={className}>
      {/*
        Wrapping rather than scrolling. A horizontal scroller shows its bar on
        every platform that draws one and turns three short words into a thing
        to push sideways; there are four pages, and on the narrowest screen
        they wrap onto a second line and are simply all visible.
      */}
      <ul className="flex flex-wrap items-center gap-1">
        {links.map((link) => (
          <li key={link.to}>
            <NavLink
              to={link.to}
              end={link.end ?? false}
              className={({ isActive }) =>
                cn(
                  "flex h-8 items-center rounded-md px-3 text-sm",
                  colorTransitionClassName,
                  focusRingClassName,
                  isActive
                    ? "bg-primary-fill font-medium text-primary-fill-foreground"
                    : "text-foreground hover:bg-foreground/[0.07]",
                )
              }
            >
              {link.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
};
